import { LogLevel } from 'consola';
import consola from 'consola/src/browser';
/**
 * Used to set the logging level.
 *
 * @example Explicitly set the level of an imported package beyond the current runtime level.
 * ```ts
 * import { LogLevel } from '@genesislcap/foundation-logger';
 * import { logger as myPackageLogger } from 'my-package';
 * myPackageLogger.level = LogLevel.Error;
 * ```
 *
 * @public
 */
export { LogLevel } from 'consola';
/**
 * The default logger options.
 * @public
 */
export const defaultLoggerOptions = {
    level: LogLevel.Debug,
};
/**
 * Creates a logger with the given name and options.
 *
 * @example Create a logger in my-package/utils.
 * ```ts
 * import { createLogger } from '@genesislcap/foundation-logger';
 * export const logger = createLogger('my-package');
 * ```
 *
 * @example Using the logger within my-package.
 * ```ts
 * import { logger } from '../utils';
 * logger.debug(`Data retrieved from ${resourceName}`);
 * logger.info(this.toggled ? 'On' : 'Off');
 * logger.warn('Not available in this browser.');
 * ```
 *
 * @param name - The name to give the logger.
 * @param options - The options to use when creating the logger.
 * @returns The resulting logger.
 * @public
 */
export function createLogger(name, options = defaultLoggerOptions) {
    const baseLogger = consola.create(options);
    const logger = baseLogger.withTag(name);
    return Object.assign(Object.assign({}, logger), { deprecated: (symbol, instruction = 'Consult docs for better alternative.', removalVersionTarget) => {
            logger.warn(`Deprecated symbol used '${symbol}'. ${instruction}`, `${removalVersionTarget ? `Will be removed in version ${removalVersionTarget}.` : ''}`);
        } });
}
